import { CommonModule, DatePipe } from '@angular/common';
import { Component, inject, OnInit, signal, ViewChild } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginator } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { LoadScheduleControlFlags, ScreenName } from '../../interfaces/securityPermissions';
import { SiteDataCaptureSites } from '../../interfaces/siteType';
import { RolePermissionsService } from '../../services/role-permissions.service';
import { RoleService } from '../../services/role.service';
import { SiteDataService } from '../../services/sitedata.service';
import { SiteDownTimeResponse } from '../../interfaces/sdcmodel';
import { formatDateOnly } from '../shared/commonutils/helper';
import { AlertDialogComponent } from '../shared/components/alert-dialog/alert-dialog.component';
import { ConfirmationDialogComponent } from '../shared/components/confirmation-dialog/confirmation-dialog.component';
import { AddeditSitedowntimeComponent } from './addedit-sitedowntime/addedit-sitedowntime.component';

@Component({
  selector: 'app-sitedowntime',
  imports: [
      CommonModule,
      FormsModule,
      ReactiveFormsModule,
      MatDialogModule,
      MatButtonModule,
      MatIconModule,
      MatFormFieldModule,
      MatInputModule,
      MatSelectModule,
      MatCardModule,
      MatTabsModule,
      MatMenuModule,
      MatTableModule,
      MatDatepickerModule
    ],
  templateUrl: './sitedowntime.component.html',
  styleUrl: './sitedowntime.component.scss'
})
export class SitedowntimeComponent implements OnInit {
  permissions = inject(RolePermissionsService);
  rolesService= inject(RoleService);
  private dialog = inject(MatDialog);
  private service = inject(SiteDataService);
  displayedColumns: string[] = [ 'actions', 'dateCaptured', 'plant', 'processName', 'equipmentCategory',
    'equipmentName', 'downtime', 'impactName', 'trigger', 'comments'];
  dataSource = new MatTableDataSource<SiteDownTimeResponse>();
  // Map that stores the rowspan for the first element of each group keyed by the element reference
  rowSpanFor = new Map<SiteDownTimeResponse, number>();
  // Map that stores the group index for every element (used for alternating group coloring)
  elementGroupIndex = new Map<SiteDownTimeResponse, number>();

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;  
  selectedSiteIdControl = new FormControl<number | null>(null);
  selectedSiteId = this.selectedSiteIdControl.valueChanges.pipe().subscribe((id) => {
    this.selectedSiteIdValue.set(id);
  });
  selectedSiteIdValue = signal<number | null>(null);
  sdcSites = signal<SiteDataCaptureSites[]>([]);
  datePipe: DatePipe = new DatePipe('en-US');
  range: FormGroup = new FormGroup({
    start: new FormControl<Date | null>(null),
    end: new FormControl<Date | null>(null),
  });
  controlPermissions: LoadScheduleControlFlags = new LoadScheduleControlFlags();

  // guard to avoid multiple subscriptions
  private sortSubscribed = false;

  ngOnInit() {
    const today = new Date();
    const firstDayOfCurrentWeek = this.getFirstDayOfWeek(today);
    firstDayOfCurrentWeek.setDate(today.getDate() - 7);
    today.setDate(today.getDate() + 2);

    const initialRange = new FormGroup({
      start: new FormControl<Date | null>(firstDayOfCurrentWeek),
      end: new FormControl<Date | null>(today)
    });
    this.range = initialRange;
    this.loadSites();
  }

  loadSites(){
    this.service.getsdcSites().subscribe((response) => {
      this.sdcSites.set(response);
      this.onLoadData();
    });

    let roles = this.rolesService.getRolesAsString();
    this.permissions.getRolePermissionsNew(roles, ScreenName.Sitedowntime, 0).subscribe({
      next: (perms: LoadScheduleControlFlags) => {
        this.controlPermissions = perms;
      }
    })
  }

  onLoadData(sid: number | null = null){    
    this.range.controls['start'].markAsDirty();
    this.range.controls['start'].markAsTouched();
    this.range.controls['end'].markAsDirty();
    this.range.controls['end'].markAsTouched();
    this.range.controls['start'].updateValueAndValidity();
    this.range.controls['end'].updateValueAndValidity();
    this.range.markAllAsTouched();
    this.range.updateValueAndValidity();

    if (this.range.invalid) {
      this.dialog.open(AlertDialogComponent, {
        data: {
          title: 'Invalid Date Range',
          message: 'Please select a valid date range.'
        },
        disableClose: true,
        panelClass: 'delete-confirmation-dialog-nowrap'
      });
      return;
    }
    if (this.range.invalid || !this.range.value.start || !this.range.value.end) {
      this.dialog.open(AlertDialogComponent, {
        data: {
          title: 'Invalid Date Range',
          message: 'Please select a valid date range.'
        },
        disableClose: true,
        panelClass: 'delete-confirmation-dialog-nowrap'
      });
      return;
    }

    const fromDate = this.range.value.start ? formatDateOnly(this.range.value.start) : '';
    const toDate = this.range.value.end ? formatDateOnly(this.range.value.end) : '';
    this.service.getsdtData(sid, fromDate, toDate).subscribe((response) => {
      console.log("sdt grid data", response);
      this.dataSource.data = response;
      this.dataSource.paginator = this.paginator;
      this.dataSource.sort = this.sort;

      // Compute row spans for the current rendered/filtered data
      this.computeRowSpans();

      // Subscribe to sort changes to recompute spans when user sorts columns
      if (this.sort && !this.sortSubscribed) {
        this.sort.sortChange.subscribe(() => {
          // give MatTableDataSource a tick to re-order filteredData
          setTimeout(() => this.computeRowSpans(), 0);
        });
        this.sortSubscribed = true;
      }
    });     
  }

  onSiteChange(){
    const siteId = this.selectedSiteIdValue();
    if(siteId !== null){
      this.onLoadData(siteId);
    }
    else{
      this.onLoadData();
    }
  }  

  onSearch(){
    const siteId = this.selectedSiteIdValue();
    if(siteId !== null){
      this.onLoadData(siteId);
    }else{
      this.onLoadData();
    }
  }

  onAddOrEdit(row: SiteDownTimeResponse | null, action: string, canShowSave: boolean) {
    const siteId = row
      ? row.siteId ?? this.sdcSites().find(site => site.siteName?.trim().toLowerCase() === row.plant?.trim().toLowerCase())?.id ?? null
      : this.selectedSiteIdValue();

    if (siteId === null) {
      this.dialog.open(AlertDialogComponent, {
        data: {
          title: 'Alert',
          message: action === 'Add'
            ? 'Please select a site before Site Downtime begin !!!'
            : 'Could not determine the site for this downtime entry.'
        },
        disableClose: true,
        panelClass: 'delete-confirmation-dialog-nowrap'
      });
      return;
    }

    const userName = localStorage.getItem("loggedUserName") ?? '';
    const siteName = siteId ? this.sdcSites().find(s => s.id === siteId)?.siteName || '' : '';

    const dialogSiteId = siteId !== null ? `VRS-${siteId}` : '';

    const dialogRef = this.dialog.open(AddeditSitedowntimeComponent, {
      data: {
        siteId: dialogSiteId,
        siteName: siteName,
        isEdit: action === 'Edit',
        dateCaptured: row ? row.dateCaptured : new Date(),
        userName: action === 'Edit' ? (row?.createdBy || userName) : userName,
        id: row ? row.id : 0,
        canShowSaveButton: canShowSave
      },
      width: '1050px',
      maxWidth: '1050px',
      disableClose: true,
      maxHeight: '90vh'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.onLoadData(this.selectedSiteIdValue());
      }
    });

  }

  /**
   * Returns a stable key for grouping rows by Date and Plant. Uses datePipe to normalize date.
   */
  private getGroupKey(row: SiteDownTimeResponse): string {
    const date = row.dateCaptured ? this.datePipe.transform(row.dateCaptured, 'yyyy-MM-dd') : '';
    const plant = (row.plant || '').trim().toLowerCase();
    return `${date}|${plant}`;
  }

  /**
   * Compute row spans for consecutive rows that share the same Date and Plant.
   * Stores the span count keyed by the first element object of each group in rowSpanFor map.
   */
  private computeRowSpans(): void {
    this.rowSpanFor.clear();
    this.elementGroupIndex.clear();
    const rows = (this.dataSource.filteredData && this.dataSource.filteredData.length)
      ? this.dataSource.filteredData
      : this.dataSource.data || [];

    let groupIndex = 0;
    for (let i = 0; i < rows.length; i++) {
      const key = this.getGroupKey(rows[i]);
      let j = i + 1;
      while (j < rows.length && this.getGroupKey(rows[j]) === key) {
        j++;
      }
      const span = j - i;
      if (span > 0) {
        // rowSpan for first element in the consecutive group
        this.rowSpanFor.set(rows[i], span);
        // assign group index to all elements in this group so we can style them consistently
        for (let k = i; k < j; k++) {
          this.elementGroupIndex.set(rows[k], groupIndex);
        }
        groupIndex++;
      }
      i = j - 1;
    }
  }

  /** Returns true when the logical group for this row is odd (used to apply alternating styles) */
  isGroupOdd(row: SiteDownTimeResponse): boolean {
    const idx = this.elementGroupIndex.get(row);
    return (idx ?? 0) % 2 === 1;
  }

  getTriggerColorClass(trigger: string | null | undefined): string {
    switch (trigger?.trim().replace(/\s+/g, ' ').toLowerCase()) {
      case 'operator + ops manager rca':
        return 'trigger-operator-ops-rca';
      case 'plant manager rca':
        return 'trigger-plant-manager-rca';
      case 'no rca needed':
        return 'trigger-no-rca';
      case 'hq rca':
        return 'trigger-hq-rca';
      default:
        return '';
    }
  }

  onDelete(row: SiteDownTimeResponse){
    const siteId = row.siteId
      ?? this.sdcSites().find(site => site.siteName?.trim().toLowerCase() === row.plant?.trim().toLowerCase())?.id
      ?? null;
    const dateCaptured = formatDateOnly(row.dateCaptured);

    if (siteId === null || !dateCaptured) {
      this.dialog.open(AlertDialogComponent, {
        data: {
          title: 'Alert',
          message: 'Could not determine the site and date for this downtime entry.'
        },
        disableClose: true,
        panelClass: 'delete-confirmation-dialog-nowrap'
      });
      return;
    }

    this.dialog.open(ConfirmationDialogComponent, {
      panelClass: 'delete-confirmation-dialog',
      data: {
        title: 'Confirm Deletion',
        message: `Are you sure you want to delete the Site Downtime entry for "${row.plant}" captured on "${this.datePipe.transform(row.dateCaptured, 'MM/dd/yyyy')}"?`,
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel'
      }
    }).afterClosed().subscribe((confirmed) => {
      if(confirmed){
        this.service.deleteSDCaptureBySiteIdAndDate(siteId, dateCaptured).subscribe((response) => {
          if(response){
            this.onSiteChange();
          }
        });
      }
    });
  }
  

  getFirstDayOfWeek(date: Date) {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day;
    d.setDate(diff);
    return d;
  }

  onKeydown(event: KeyboardEvent) {
    if(event.ctrlKey && event.metaKey){return;} 
    if(!/[0-9\/]/.test(event.key) && event.key.length === 1){
      event.preventDefault();
    }
    setTimeout(() => {
      this.range.updateValueAndValidity();
      this.range.markAllAsTouched();
    });
  }
    
  dateformat(event: Event){
    const input = event.target as HTMLInputElement;
    let value = input.value;

    if(!value) return;
    const parts = value.split('/');
    if(parts.length !== 3) return;

    let [month, day, year] = parts.map(part => part.padStart(2, '0'));
    if(month.length === 1)  month = '0' + month;
    if(day.length === 1)  day = '0' + day;
    if(year.length === 2) year = '20' + year;
    input.value = `${month}/${day}/${year}`;
  }

}
